import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { PageBar, Content } from '../components/Layout'
import { Panel } from '../components/ui'
import { api } from '../lib/api'
import type { AppSettings } from '../types'

type State = 'live' | 'next' | 'later'

interface Item {
  title: string
  state: State
  what: string
  why: string
}

const ITEMS: Item[] = [
  {
    title: 'Triage every message on arrival',
    state: 'live',
    what: 'A local classifier sorts each enquiry into client, education, spam or needs-triage in under a millisecond, then tags the segment.',
    why: 'Half of what arrives is not a customer. Without this, Luis gets a notification for all 94 submissions a year instead of the 45 that are real.',
  },
  {
    title: 'Reply suggestions',
    state: 'live',
    what: 'Every enquiry opens with three or four complete, segment-aware replies. Click one, edit a word, send.',
    why: 'The slowest part of answering is starting. A blank box at 11pm after a session is how enquiries end up answered tomorrow — or never.',
  },
  {
    title: 'Silence alarm',
    state: 'live',
    what: 'If nothing arrives for seven days — not even spam — the overview says so.',
    why: 'The contact form died in 2024 and nobody noticed for five months. Roughly 18 enquiries lost because nothing was watching.',
  },
  {
    title: 'Invoice from a booking',
    state: 'live',
    what: 'One click turns a completed session into a numbered invoice with the right rate and due date.',
    why: 'Replaces the spreadsheet that costs real hours every month.',
  },
  {
    title: 'Instant acknowledgement',
    state: 'next',
    what: 'An automatic first reply within minutes of an enquiry landing, chosen by segment. No human involved.',
    why: 'Several enquiries ask to book the same week, and whoever replies first usually gets the session. With one engineer in sessions all day, this is the only thing answering between 10 and 6.',
  },
  {
    title: 'Mailbox reconciliation',
    state: 'next',
    what: 'Read the Sent folder so replies made from Gmail are detected and the thread stops showing as unanswered.',
    why: 'Luis is the only person who replies, and he is often mid-session. A reply sent from his phone should not leave the thread showing as unanswered on Jerry’s screen.',
  },
  {
    title: 'Deposit and reminder chase',
    state: 'next',
    what: 'Automatic nudge when a deposit is unpaid, and a reminder to the client and the engineer before the session.',
    why: 'Held bookings that never confirm are dead inventory nobody is watching.',
  },
  {
    title: 'Xero export',
    state: 'later',
    what: 'Push invoices to Xero rather than re-keying them.',
    why: 'We build invoicing because nothing fits a studio. We do not build a ledger.',
  },
  {
    title: 'Trends to drafts',
    state: 'later',
    what: 'Watch what is happening in the industry, work out what is worth saying, and produce post drafts for approval.',
    why: 'The differentiated half of the marketing idea — and reusable across other ventures.',
  },
]

const LABEL: Record<State, string> = { live: 'Live', next: 'Next', later: 'Later' }

export function Automation() {
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => api.get<AppSettings>('/settings') })

  return (
    <>
      <PageBar title="Automation" />
      <Content>
        <Panel pad>
          <p className="lede">
            The point of automating anything here is <b>attention</b>, not cleverness.
            <b> Luis runs the studio on his own</b> and Jerry supervises — so for eight hours at
            a stretch there is nobody free to answer anything. Every process below exists to
            cover a gap that one person, mid-session, cannot reasonably be expected to watch.
          </p>
          <p className="hint" style={{ marginTop: 10 }}>
            Rule we hold to: <b>a bot may draft and a bot may acknowledge, but only a person
            quotes a price or commits to a date.</b> A studio cannot have software confidently
            promising a booked-out Saturday.
          </p>
        </Panel>

        {(['live', 'next', 'later'] as State[]).map((state) => (
          <section key={state}>
            <h2 className="sec">{LABEL[state]}</h2>
            <div className="autolist">
              {ITEMS.filter((i) => i.state === state).map((item) => (
                <Panel key={item.title} pad className="autocard">
                  <div className="autohead">
                    <b>{item.title}</b>
                    <span className={`badge auto-${state}`}>{LABEL[state]}</span>
                  </div>
                  <p className="autowhat">{item.what}</p>
                  <p className="autowhy"><span>Why</span> {item.why}</p>
                </Panel>
              ))}
            </div>
          </section>
        ))}

        <p className="hint">
          Drafting currently runs as <b>{settings.data?.drafting ?? '…'}</b> and email as{' '}
          <b>{settings.data?.email_backend ?? '…'}</b>. See <Link to="/lab">Lab</Link> for the
          hardware and research side.
        </p>
      </Content>
    </>
  )
}
